import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    FormControl,
    FormGroup,
    FormLabel,
    Paper,
    TextField,
    Typography,
    Divider,
    Checkbox,
    Select,
    MenuItem,
    Alert,
} from '@mui/material';

interface Step4Props {
    hasCmdrs: boolean | null;
    disabled?: boolean;
    onNext: () => void;
    onBack: () => void;
    onChange: (data: Step4CmdrsData) => void;
    data: Step4CmdrsData;
}

export interface ChallengeOrder {
    value: string;
    order: number;
}

export interface Step4CmdrsData {
    selectedChallenges: string[];
    challengesOrder: ChallengeOrder[];
    otherChallenge: string;
    referenceYear?: number;
}

const challengesOptions = [
    { label: 'Obter acesso ao crédito rural', value: 'credito_rural' },
    { label: 'Mercado para comercializar a produção', value: 'mercado' },
    { label: 'Pouco envolvimento dos jovens', value: 'jovens' },
    { label: 'Envelhecimento da população de agricultoras(es) familiares', value: 'envelhecimento' },
    { label: 'Falta de mão de obra', value: 'mao_obra' },
    { label: 'Custo de produção elevado', value: 'custo_producao' },
    { label: 'Falta de informação', value: 'informacao' },
    { label: 'Regularização ambiental', value: 'ambiental' },
    { label: 'Regularização fiscal', value: 'fiscal' },
    { label: 'Regularização fundiária', value: 'fundiaria' },
    { label: 'Regularização sanitária', value: 'sanitaria' },
    { label: 'Outro', value: 'other' },
];

const Step4: React.FC<Step4Props> = ({ hasCmdrs, disabled, onNext, onBack, onChange, data }) => {
    const [selectedChallenges, setSelectedChallenges] = useState<string[]>(data?.selectedChallenges || []);
    const [challengesOrder, setChallengesOrder] = useState<ChallengeOrder[]>(data?.challengesOrder || []);
    const [otherChallenge, setOtherChallenge] = useState(data?.otherChallenge || '');
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Atualiza os estados internos quando o prop data mudar
    useEffect(() => {
        setSelectedChallenges(data?.selectedChallenges || []);
        setChallengesOrder(data?.challengesOrder || []);
        setOtherChallenge(data?.otherChallenge || '');
    }, [data]);

    // Atualiza o estado global sempre que algum campo mudar
    useEffect(() => {
        onChange({
            selectedChallenges,
            challengesOrder,
            otherChallenge,
            referenceYear: new Date().getFullYear(),
        });
        // eslint-disable-next-line
    }, [selectedChallenges, challengesOrder, otherChallenge]);

    // Handler para seleção dos desafios
    const handleChallengeChange = (value: string) => {
        setSelectedChallenges(prev => {
            if (prev.includes(value)) {
                setChallengesOrder(orders => {
                    // Remove o desafio
                    const filtered = orders.filter(o => o.value !== value);
                    // Reordena as prioridades para manter sequência contínua
                    return filtered.map((item, idx) => ({
                        ...item,
                        order: idx + 1,
                    }));
                });
                if (value === 'other') setOtherChallenge('');
                return prev.filter(v => v !== value);
            } else {
                setChallengesOrder(orders => [
                    ...orders,
                    { value, order: orders.length + 1 }
                ]);
                return [...prev, value];
            }
        });
    };

    const handleOrderChange = (value: string, newOrder: number) => {
        setChallengesOrder(prev => {
            const current = prev.find(item => item.value === value);
            const other = prev.find(item => item.order === newOrder);

            if (!current) return prev;

            return prev.map(item => {
                if (item.value === value) {
                    return { ...item, order: newOrder };
                }
                if (other && item.value === other.value) {
                    return { ...item, order: current.order };
                }
                return item;
            });
        });
    };

    // Validação
    const validate = () => {
        const newErrors: { [key: string]: string } = {};
        if (selectedChallenges.length === 0) newErrors.selectedChallenges = 'Selecione pelo menos um desafio e atribua prioridade única a cada um.';
        if (selectedChallenges.includes('other') && !otherChallenge) newErrors.otherChallenge = 'Descreva o desafio';
        // Checa se todas as ordens estão preenchidas e únicas
        const orders = challengesOrder.map(o => o.order);
        if (
            selectedChallenges.length !== challengesOrder.length ||
            new Set(orders).size !== orders.length ||
            orders.some(o => !o || o < 1 || o > selectedChallenges.length)
        ) {
            newErrors.challengesOrder = 'Defina uma ordem única para cada desafio selecionado';
        }
        return newErrors;
    };

    const handleSubmit = () => {
        const validation = validate();
        setErrors(validation);
        if (Object.keys(validation).length > 0) return;
        setErrors({});
        onNext();
    };

    if (hasCmdrs === false) {
        return (
            <Box>
                <Paper elevation={2} sx={{ p: 3, mb: 4, opacity: 0.6 }}>
                    <Typography variant="h6" gutterBottom>
                        Desafios da Agricultura Familiar
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography color="text.secondary">
                        Esta aba está desabilitada pois o município não possui CMDRS.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box>
            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Desafios da Agricultura Familiar
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <TextField
                    label="Ano referência do Questionário"
                    value={new Date().getFullYear()}
                    fullWidth
                    margin="normal"
                    InputProps={{ readOnly: true }}
                />

                <Alert
                    severity='info'
                    sx={{ marginY: '2rem', backgroundColor: '#EAEFF9' }}
                >
                    <Typography
                        variant='body2'
                        fontSize='16px'
                        textAlign='left'
                        lineHeight={1.5}
                    >
                        {`Atribua uma prioridade numérica aos desafios selecionados, sendo 1 o mais relevante. Não é permitido repetir a mesma prioridade entre itens diferentes.`}
                    </Typography>
                </Alert>

                <FormControl component="fieldset" fullWidth margin="normal" required>
                    <FormLabel
                        component="legend"
                        sx={{ color: 'text.primary', fontWeight: 600 }}
                    >
                        Marque os desafios enfrentados e ordene conforme a importância (1 = mais relevante):
                    </FormLabel>
                    <FormGroup>
                        {challengesOptions.map(opt => (
                            <Box key={opt.value} display="flex" alignItems="center" mb={1}>
                                <Checkbox
                                    checked={selectedChallenges.includes(opt.value)}
                                    onChange={() => handleChallengeChange(opt.value)}
                                    value={opt.value}
                                    disabled={disabled}
                                />
                                <Typography sx={{ minWidth: 0, flex: 1 }}>{opt.label}</Typography>
                                {selectedChallenges.includes(opt.value) && (
                                    <Select
                                        size="small"
                                        sx={{ width: 80, ml: 2 }}
                                        value={
                                            challengesOrder.find(o => o.value === opt.value)?.order || ''
                                        }
                                        onChange={e =>
                                            handleOrderChange(opt.value, Number(e.target.value))
                                        }
                                        disabled={disabled}
                                    >
                                        {[...Array(selectedChallenges.length)].map((_, idx) => (
                                            <MenuItem key={idx + 1} value={idx + 1}>
                                                {idx + 1}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            </Box>
                        ))}
                    </FormGroup>
                    {errors.selectedChallenges && (
                        <Typography color="error">{errors.selectedChallenges}</Typography>
                    )}
                    {errors.challengesOrder && (
                        <Typography color="error">{errors.challengesOrder}</Typography>
                    )}
                </FormControl>
                {selectedChallenges.includes('other') && (
                    <TextField
                        label="Outro (descreva)"
                        value={otherChallenge}
                        onChange={e => {
                            if (e.target.value.length <= 500) {
                                setOtherChallenge(e.target.value);
                            }
                        }}
                        error={!!errors.otherChallenge}
                        helperText={
                            errors.otherChallenge
                                ? errors.otherChallenge
                                : `${otherChallenge.length}/500 caracteres`
                        }
                        fullWidth
                        margin="dense"
                        disabled={disabled}
                        required
                        multiline
                        inputProps={{
                            maxLength: 500,
                        }}
                        sx={{
                            mt: 2,
                            background: '#fff',
                            borderRadius: 1,
                            '& .MuiInputBase-root': {
                                minHeight: 80,
                                alignItems: 'flex-start',
                            },
                        }}
                    />
                )}

                <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={onBack}
                        disabled={disabled}
                    >
                        Voltar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={disabled}
                    >
                        Salvar e continuar
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default Step4;